/**
 * Language — the app's UI locale. `en` (default) ⇄ `de` (`100-settings.md` §1, `ui.language`).
 *
 * Modeled on `stores/density.ts`, and for the same reasons: the value must be readable
 * **synchronously**, before the first React render, so a German user never sees a flash of
 * English. That rules out TanStack Query and IndexedDB — localStorage is the read path. It is a
 * few bytes of per-device UI preference holding no credentials. It is *also* mirrored to
 * `/api/settings` (via `useLanguagePreference`) so a new browser inherits it; localStorage stays
 * the fast path either way (`111-frontend-architecture.md` §5).
 *
 * The `<html lang>` attribute is stamped from here (and pre-painted in `index.html`) so the
 * document advertises its language to the browser and to assistive technology.
 *
 * ## Two consumers, one source of truth
 *
 *  - React components subscribe through `useTranslation()` (a `useSyncExternalStore` over this
 *    module), so a language switch re-renders them.
 *  - Non-React code — outbox toasts, mutation hooks that fire outside the render tree — calls the
 *    standalone `translate()` in `t.ts`, which reads `getLang()` here directly.
 */
export type Language = 'en' | 'de';

const STORAGE_KEY = 'rn.lang';

const listeners = new Set<() => void>();

/**
 * The live value, mirrored into a module variable so `translate()` (non-React) can read it
 * without touching localStorage on every call. Kept in sync by `setLang` and by `read`.
 */
let currentLang: Language = readStored();

function readStored(): Language {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'de' ? 'de' : 'en';
  } catch {
    // localStorage can throw in locked-down/private contexts; fall back to English.
    return 'en';
  }
}

/** The current language, for non-React callers. Cheap — reads the module variable. */
export function getLang(): Language {
  return currentLang;
}

export function setLang(lang: Language): void {
  currentLang = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Non-persistent is acceptable; the value still applies for this session.
  }
  document.documentElement.lang = lang;
  for (const listener of listeners) listener();
}

/** For `useSyncExternalStore`. Re-reads localStorage so a value set in another tab is picked up. */
export function read(): Language {
  currentLang = readStored();
  return currentLang;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
